import { Box, Button, Icon, Modal } from '@platform/ui-library';
import { FC, useCallback, useEffect, useRef, useState } from 'react';
import { CodeEditor, CodeEditorLanguage } from '@app/form_components';
import { useServices } from '@app/hooks';
import { ConversationRole, GenerateRequest } from '@app/types';
import styles from './AiCodeGenerationDialog.module.scss';

export type AiCodeGenerationDialogProps = (
  Pick<GenerateRequest, 'currentCode' | 'functionInputs'>
  & {
    /** Whether the dialog is open */
    isOpen: boolean;
    /** Callback when user accepts generated code */
    onAccept: (code: string) => void;
    /** Callback to close the dialog */
    onClose: () => void;
  }
);

enum GenerationState {
  idle = 'idle',
  loading = 'loading',
  success = 'success',
  error = 'error'
}

type ErrorInfo = {
  message: string;
  details?: string;
};

/**
 * AI Code Generation Dialog
 *
 * Allows users to describe what they want to accomplish and generates
 * JavaScript code using Gemini AI. Supports iterative refinement through
 * multi-turn conversation.
 */
export const AiCodeGenerationDialog: FC<AiCodeGenerationDialogProps> = ({
  currentCode,
  functionInputs,
  isOpen,
  onAccept,
  onClose
}) => {
  const { geminiService, globalModalsService } = useServices();
  const [userPrompt, setUserPrompt] = useState('');
  const [generatedCode, setGeneratedCode] = useState<string | null>(null);
  const [showDiff, setShowDiff] = useState(false);
  const [summary, setSummary] = useState<string | null>(null);
  const [wasTruncated, setWasTruncated] = useState(false);
  const [error, setError] = useState<ErrorInfo | null>(null);
  const [showErrorDetails, setShowErrorDetails] = useState(false);
  const [state, setState] = useState(GenerationState.idle);
  const [conversationHistory, setConversationHistory] = useState<GenerateRequest['conversationHistory']>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleStartOver = useCallback(() => {
    setUserPrompt('');
    setGeneratedCode(null);
    setShowDiff(false);
    setSummary(null);
    setWasTruncated(false);
    setError(null);
    setShowErrorDetails(false);
    setState(GenerationState.idle);
    setConversationHistory([]);
  }, []);

  // Focus textarea when dialog opens.
  useEffect(() => {
    if (isOpen) {
      textareaRef.current?.focus();
    }
  }, [isOpen]);

  // Reset state when dialog closes.
  useEffect(() => {
    if (!isOpen) {
      handleStartOver();
    }
  }, [isOpen, handleStartOver]);

  const handleGenerate = useCallback(async () => {
    if (!userPrompt.trim()) {
      return;
    }

    setState(GenerationState.loading);
    setError(null);
    setShowErrorDetails(false);

    try {
      const data = await geminiService.generate({
        conversationHistory,
        currentCode: generatedCode || currentCode,
        functionInputs,
        userPrompt: userPrompt.trim()
      });

      // Update conversation history.
      setConversationHistory([
        ...conversationHistory,
        { role: ConversationRole.user, content: userPrompt.trim() },
        { role: ConversationRole.model, content: data.code }
      ]);

      setGeneratedCode(data.code);
      setSummary(data.summary);
      setWasTruncated(data.wasTruncated ?? false);
      setState(GenerationState.success);
      // Clear input for next iteration.
      setUserPrompt('');
    } catch (err) {
      setError({
        message: (err instanceof Error) ? err.message : 'An unexpected error occurred'
      });
      setState(GenerationState.error);
    }
  }, [geminiService, userPrompt, currentCode, generatedCode, functionInputs, conversationHistory]);

  const handleAccept = useCallback(() => {
    if (generatedCode) {
      onAccept(generatedCode);
      onClose();
    }
  }, [generatedCode, onAccept, onClose]);

  const handleClose = useCallback(async () => {
    const hasUnsavedChanges = generatedCode || state === GenerationState.loading;
    if (
      !hasUnsavedChanges
      || await globalModalsService.confirmDiscardChanges('You have changes being generated that will be lost.')
    ) {
      onClose();
    }
  }, [generatedCode, state, globalModalsService, onClose]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    // Submit on Enter (without Shift).
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleGenerate();
    }
  }, [handleGenerate]);

  if (!isOpen) {
    return null;
  }

  return (
    <Modal
      className={styles.modal}
      handleClose={handleClose}
      isOpen={isOpen}
    >
      <Box className={styles.container} theme="bigPadding">
        <div className={styles.header}>
          <div className={styles.titleRow}>
            <h2>AI Code Assistant</h2>
            <span className={styles.modelBadge}>Gemini Flash</span>
          </div>
          {!generatedCode && (
            <p className={styles.subtitle}>
              Describe what you want your code to do, ask questions about your current code, or request modifications.
              This assistant has knowledge of the Process Engine API.
            </p>
          )}
        </div>

        {/* Function inputs context */}
        {functionInputs.length > 0 && (
          <div className={styles.contextSection}>
            <span className={styles.contextLabel}>Available variables:</span>
            <div className={styles.variableTags}>
              {functionInputs.map((input, idx) => (
                <span key={idx} className={styles.variableTag}>
                  {input.argumentName}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Conversation history */}
        {conversationHistory.length > 0 && (
          <div className={styles.conversationHistory}>
            {conversationHistory.filter((turn) => turn.role === ConversationRole.user).map((turn, idx) => (
              <div key={idx} className={styles.historyItem}>
                <i className="fe icon-user" />
                <span>{turn.content}</span>
              </div>
            ))}
          </div>
        )}

        {/* User input */}
        <div className={styles.inputSection}>
          <textarea
            ref={textareaRef}
            className={styles.promptInput}
            placeholder={
              (generatedCode) ? (
                'Ask a question or describe how to modify the code...'
              ) : 'Describe what you want, or ask about your current code...'
            }
            value={userPrompt}
            onChange={(e) => setUserPrompt(e.target.value)}
            onKeyDown={handleKeyDown}
            disabled={state === GenerationState.loading}
            rows={(generatedCode) ? 1 : 3}
          />
          <Button
            className={styles.generateButton}
            disabled={!userPrompt.trim() || state === GenerationState.loading}
            onClick={handleGenerate}
            type="button"
          >
            {(state === GenerationState.loading) ? (
              <>
                <span className="glyphicon glyphicon-refresh glyphicon-refresh-animate" style={{ marginRight: 4 }} />
                <span>Generating...</span>
              </>
            ) : (
              <>
                <i className="fe icon-zap" style={{ marginRight: 4 }} />
                {(generatedCode) ? 'Refine' : 'Generate'}
              </>
            )}
          </Button>
        </div>

        {/* Error message */}
        {error && (
          <div className={styles.errorMessage}>
            <Icon icon="errorColor" />
            <div className={styles.errorContent}>
              <span>{error.message}</span>
              {error.details && (
                <>
                  <button
                    className={styles.errorDetailsToggle}
                    onClick={() => setShowErrorDetails(!showErrorDetails)}
                    type="button"
                  >
                    {(showErrorDetails) ? 'Hide details' : 'Show details'}
                  </button>
                  {showErrorDetails && (
                    <pre className={styles.errorDetails}>{error.details}</pre>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Generated code preview */}
        {generatedCode && (
          <div className={styles.resultSection}>
            <div className={styles.resultHeader}>
              <div className={styles.resultTitleRow}>
                <h3>Generated Code</h3>
                {currentCode.trim() && (
                  <button
                    className={`${styles.diffToggle} ${(showDiff) ? styles.active : ''}`}
                    onClick={() => setShowDiff(!showDiff)}
                    title={(showDiff) ? 'Hide diff' : 'Show diff against current code'}
                    type="button"
                  >
                    <i className="fe icon-git-merge" />
                    {(showDiff) ? 'Hide Diff' : 'Diff'}
                  </button>
                )}
              </div>
              {summary && (
                <p className={styles.summary}>
                  <Icon icon="info" />
                  {summary}
                </p>
              )}
              {wasTruncated && (
                <p className={styles.truncationWarning}>
                  <Icon icon="warningColor" />
                  The response was truncated due to length limits. Try a more specific prompt for better results.
                </p>
              )}
            </div>
            <div className={styles.codePreview}>
              <CodeEditor
                language={CodeEditorLanguage.javascript}
                onChange={(code) => setGeneratedCode(code)}
                originalValue={(showDiff) ? currentCode : undefined}
                value={generatedCode}
              />
            </div>
          </div>
        )}

        {/* Action buttons */}
        <div className={styles.actions}>
          {generatedCode && (
            <Button
              className={styles.startOverButton}
              onClick={handleStartOver}
              theme="outline"
              type="button"
            >
              Start Over
            </Button>
          )}
          <div className={styles.rightActions}>
            <Button onClick={handleClose} theme="outline" type="button">
              Cancel
            </Button>
            {generatedCode && (
              <Button
                className={styles.acceptButton}
                onClick={handleAccept}
                type="button"
              >
                <i className="fe icon-check" style={{ marginRight: 4 }} />
                Accept Code
              </Button>
            )}
          </div>
        </div>
      </Box>
    </Modal>
  );
};
